import type { RefObject, ComponentProps } from 'react'

import {
  useRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react'

import * as React from 'react'
import { defineMessages, useIntl } from 'react-intl'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { GlobalSearchDispatchContext, GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import useCruiseSearchFacets from 'hooks/Cruise/useCruiseSearchFacets'
import type { CruiseDepartureSelectDesktopRef } from './CruiseDepartureSelectDesktop'
import CruiseDepartureSelectDesktop from './CruiseDepartureSelectDesktop'
import CruiseDepartureSelectMobile from './CruiseDepartureSelectMobile'
import { getCruiseExtraFilters, getGlobalFacetFilters } from 'lib/cruises/cruiseUtils'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import GeoContext from 'contexts/geoContext'
import { sortBy } from 'lib/array/arrayUtils'
import useGlobalSearchTypeahead from 'hooks/GlobalSearch/useGlobalSearchTypeahead'
import { TOUR_V2_LOCATION_SEARCH_TYPEAHEAD_TYPES, TOUR_V2_SEARCH_TYPES } from 'constants/tours'
import useQueryParams from 'hooks/useQueryParams'
import { omitKeys } from 'lib/object/objectUtils'
import { CRUISE_SEARCH_DEPARTURE_REGIONS } from 'constants/config/region'
import type DropdownSheet from 'LuxKit/Dropdown/Sheet/DropdownSheet'
import { trackClientEvent } from 'analytics/analytics'
import { CruiseSearchInputProvider, useCruiseSearchInput } from '../CruiseSearchInputContext'

export interface DeparturePortMapped {
  searchItem: App.SearchItem
  isSelected: boolean
  isDisabled: boolean
  order?: number
  regionIdentifier?: string
}

export interface CruiseDepartureSelectRef {
  focus: () => void
}

interface Props {
  onChange: (values: App.CruiseGlobalFilters) => void
  dropdownAnchorRef?: RefObject<HTMLElement | null>
  drawerMode?: boolean
  isChipMode?: boolean
  shouldDisableRegionTabs?: boolean
  dropdownSize?: ComponentProps<typeof DropdownSheet>['size']
  initialFilters?: App.CruiseInitialFilters
  disableAutoFocus?: boolean
}

const SET_SECONDARY_SEARCH_ITEMS = GlobalSearchStateActions.SET_SECONDARY_SEARCH_ITEMS

const cruiseDepartureRegionMessages = defineMessages({
  'australia-new-zealand': {
    id: 'Owy2neWgLNL4qRaZguRi',
    defaultMessage: 'Australia & New Zealand',
    description: 'Cruise departure region tab label for Australia and New Zealand',
  },
  europe: {
    id: 'r97NYEiUZ0Ewa90wxcTH',
    defaultMessage: 'Europe',
    description: 'Cruise departure region tab label for Europe in the cruise departures filter tabs',
  },
  'north-america': {
    id: 'TnGoZiL-OrDkCv_AyQEr',
    defaultMessage: 'North America',
    description: 'Cruise departure region tab label for North America',
  },
  'latin-america-caribbean': {
    id: 'wdgkXCeWpJGuSlkXEiaL',
    defaultMessage: 'Latin America & Caribbean',
    description: 'Cruise departure region tab label for Latin America and Caribbean',
  },
  'asia-pacific': {
    id: 'hAeh1d4HLd-0A_9AEgLJ',
    defaultMessage: 'Asia & the Pacific',
    description: 'Cruise departure region tab label for Asia and the Pacific',
  },
})

const CruiseDepartureSelectInner = forwardRef<CruiseDepartureSelectRef, Props>(({
  drawerMode,
  isChipMode,
  onChange,
  dropdownAnchorRef,
  dropdownSize,
  shouldDisableRegionTabs,
  initialFilters,
  disableAutoFocus,
}, ref) => {
  const intl = useIntl()
  const queryParams = useQueryParams()
  const inputRef = useRef<HTMLDivElement>(null)
  const desktopInputRef = useRef<CruiseDepartureSelectDesktopRef>(null)
  const departureRegions = useMemo(() => {
    return CRUISE_SEARCH_DEPARTURE_REGIONS.map((region) => {
      return {
        ...region,
        name: intl.formatMessage(cruiseDepartureRegionMessages[region.identifier]),
      }
    })
  }, [intl])
  const { isOpen } = useCruiseSearchInput()

  useImperativeHandle(ref, () => ({
    focus: () => {
      desktopInputRef.current?.focus()
    },
  }), [])
  const [isAllSelected, setIsAllSelected] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedRegion, setSelectedRegion] = useState(departureRegions[0])
  const cruiseExtraFilters = getCruiseExtraFilters(queryParams)

  useEffect(() => {
    setSelectedRegion((currentSelectedRegion) => {
      return departureRegions.find(({ identifier }) => identifier === currentSelectedRegion.identifier) || departureRegions[0]
    })
  }, [departureRegions])

  const searchDispatch = useContext(GlobalSearchDispatchContext)
  const { geoCountryCode } = useContext(GeoContext)
  const { suggestedSearchItems, secondarySearchItems, searchItems, cruiseLines } = useContext(GlobalSearchStateContext)
  const globalFilters = useContext(GlobalSearchStateContext)
  const facetFilters = getGlobalFacetFilters(globalFilters)

  const [allFacetsV2] = useCruiseSearchFacets({
    facetTypes: ['departure_ports'],
    ...(initialFilters || {}),
  }, { isDisabled: !isOpen })

  const [filteredFacetsV2, fetching] = useCruiseSearchFacets({
    facetTypes: ['departure_ports'],
    ...omitKeys(
      ['departureId', 'departureIds', 'departureName', 'departureNames'],
      { ...(initialFilters || {}), ...cruiseExtraFilters, ...facetFilters },
    ),
  }, { isDisabled: !isOpen })

  const facets = allFacetsV2.departurePorts
  const filteredFacets = filteredFacetsV2.departurePorts
  useGlobalSearchTypeahead({
    search: searchTerm,
    searchTypes: TOUR_V2_SEARCH_TYPES,
    typeaheadTypes: TOUR_V2_LOCATION_SEARCH_TYPEAHEAD_TYPES,
  })

  const checkIsSelected = useCallback((searchItem: App.SearchItem) => {
    return isAllSelected ||
      secondarySearchItems.some(({ value }) => value === searchItem.value)
  }, [isAllSelected, secondarySearchItems])

  const checkIsDisabled = useCallback((searchItem: App.SearchItem) => {
    return !filteredFacets.some(({ slug }) => slug === searchItem.value)
  }, [filteredFacets])

  const getRegionDeparturePort = useCallback((departurePort: string) => {
    const split = departurePort.split(',')
    const lastName = split[split.length - 1]
    return lastName.trim()
  }, [])

  const mapDeparturePort = useCallback((departurePort: App.PortFacetV2Item) => {
    const [shortName, ...info] = departurePort.label.split(', ')
    const departureInfo = info.join(', ')

    const searchItem = {
      searchType: 'departurePort',
      value: departurePort.slug,
      format: {
        mainText: shortName,
        secondaryText: departureInfo,
      },
    } as App.SearchItem

    const isSelected = checkIsSelected(searchItem)
    const isDisabled = checkIsDisabled(searchItem)
    const country = getRegionDeparturePort(departurePort.label)
    const region = departureRegions.find(({ countries }) => countries.includes(country))
    return {
      searchItem,
      isSelected,
      isDisabled,
      order: departurePort.count,
      regionIdentifier: region?.identifier!,
    } as DeparturePortMapped
  }, [checkIsDisabled, checkIsSelected, departureRegions, getRegionDeparturePort])

  const filterFacetsByRegion = useCallback((region: App.CruiseDepartureRegion) => {
    return [...filteredFacets, ...facets].filter((departurePort) => {
      const isOnTabRegion = region.countries.some((country: string) => {
        const lastName = getRegionDeparturePort(departurePort.label)
        return lastName.toLowerCase().includes(country.toLowerCase())
      })

      return (
        isOnTabRegion &&
        !region.placeIds.includes(departurePort.slug)
      )
    })
  }, [facets, filteredFacets, getRegionDeparturePort])

  const regionsByMoreResults = useMemo(() =>
    sortBy(departureRegions, (region) =>
      filteredFacets
        .filter(({ slug }) => region.placeIds.includes(slug))
        .reduce((acc, { count }) => acc + (count || 0), 0),
    'desc'),
  [departureRegions, filteredFacets])

  // when there are no filters selected on the page, the first tab must be the tab closest to the user's location
  // when there are filters, the first tab must be the tab with the most results
  const regionsWithoutIdentifier = useMemo(() => {
    if (geoCountryCode &&
      !searchItems.length &&
      !cruiseLines.length) {
      const region = departureRegions.find(({ countriesCodes }) => countriesCodes.includes(geoCountryCode.toUpperCase()))
      if (region) {
        const regionsWithoutRegion = regionsByMoreResults.filter(({ identifier }) => identifier !== region.identifier)
        return [region, ...regionsWithoutRegion]
      }
    }

    return regionsByMoreResults
  }, [cruiseLines.length, departureRegions, geoCountryCode, regionsByMoreResults, searchItems.length])

  const departurePorts = useMemo(() => {
    if (!searchTerm) {
      return filterFacetsByRegion(selectedRegion)
    }

    let filtered = filteredFacets.filter(({ label }) => label.toLowerCase().includes(searchTerm.toLowerCase()))
    if (!filtered.length) {
      filtered = filteredFacets.filter(({ label }) => {
        return suggestedSearchItems?.find((suggestedValue) => {
          return label.includes(suggestedValue?.format?.secondaryText!)
        })
      })
    }
    return filtered.slice(0, 100)
  }, [searchTerm, filteredFacets, filterFacetsByRegion, selectedRegion, suggestedSearchItems])

  const groupedDeparturePorts = useMemo(() => {
    return departurePorts.reduce((acc: Array<App.PortFacetV2Item>, departurePort) => {
      const departureName = departurePort.label.split(', ')[0]
      const existsInList = acc.some(({ label }) => departureName.includes(label.split(', ')[0]))
      if (!existsInList) {
        acc.push(departurePort)
      }
      return acc
    }, [])
  }, [departurePorts])

  // return a list with departure ports sorted by order and disabled ports at the end
  const searchDeparturePorts = useMemo(() =>
    sortBy(groupedDeparturePorts.map(mapDeparturePort),
      (departurePort) => {
        if (departurePort.isDisabled) {
          return -1
        }

        return departurePort.order
      }, 'desc'),
  [groupedDeparturePorts, mapDeparturePort])

  const handleSearchTerm = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    trackClientEvent({
      subject: 'filter_input',
      action: 'input',
      category: 'cruise_departure_filter',
      type: 'interaction',
    })
    setSearchTerm(e.target.value)
  }, [setSearchTerm])

  const handleOnChange = useCallback((secondarySearchItems: Array<App.SearchItem>) => {
    searchDispatch({
      type: SET_SECONDARY_SEARCH_ITEMS,
      secondarySearchItems,
    })
    setIsAllSelected(false)
  }, [searchDispatch, setIsAllSelected])

  return (
    <div ref={inputRef}>
      <CSSBreakpoint max="mobile">
        <CruiseDepartureSelectMobile
          onChange={onChange}
          regions={regionsWithoutIdentifier}
          setIsAllSelected={setIsAllSelected}
          isAllSelected={isAllSelected}
          setSearchTerm={setSearchTerm}
          searchTerm={searchTerm}
          handleSearchTerm={handleSearchTerm}
          handleOnChange={handleOnChange}
          fetching={fetching}
          searchDeparturePorts={searchDeparturePorts}
          drawerMode={drawerMode}
          setSelectedRegion={setSelectedRegion}
          filterFacetsByRegion={filterFacetsByRegion}
          mapDeparturePort={mapDeparturePort}
          isChipMode={isChipMode}
          initialFilters={initialFilters}
          disableAutoFocus={disableAutoFocus}
        />
      </CSSBreakpoint>

      <CSSBreakpoint min="tablet">
        {drawerMode && <CruiseDepartureSelectMobile
          onChange={onChange}
          regions={regionsWithoutIdentifier}
          setIsAllSelected={setIsAllSelected}
          isAllSelected={isAllSelected}
          setSearchTerm={setSearchTerm}
          searchTerm={searchTerm}
          handleSearchTerm={handleSearchTerm}
          handleOnChange={handleOnChange}
          fetching={fetching}
          searchDeparturePorts={searchDeparturePorts}
          drawerMode={drawerMode}
          setSelectedRegion={setSelectedRegion}
          filterFacetsByRegion={filterFacetsByRegion}
          mapDeparturePort={mapDeparturePort}
          isChipMode={isChipMode}
          initialFilters={initialFilters}
          disableAutoFocus={disableAutoFocus}
        />}

        {!drawerMode && <CruiseDepartureSelectDesktop
          ref={desktopInputRef}
          regions={regionsWithoutIdentifier}
          dropdownAnchorRef={dropdownAnchorRef || inputRef}
          onChange={onChange}
          setIsAllSelected={setIsAllSelected}
          isAllSelected={isAllSelected}
          setSearchTerm={setSearchTerm}
          searchTerm={searchTerm}
          setSelectedRegion={setSelectedRegion}
          selectedRegion={selectedRegion}
          handleSearchTerm={handleSearchTerm}
          handleOnChange={handleOnChange}
          searchDeparturePorts={searchDeparturePorts}
          isChipMode={isChipMode}
          fetching={fetching}
          dropdownSize={dropdownSize}
          shouldDisableRegionTabs={shouldDisableRegionTabs}
          initialFilters={initialFilters}
        />}
      </CSSBreakpoint>
    </div>
  )
})

CruiseDepartureSelectInner.displayName = 'CruiseDepartureSelectInner'

const CruiseDepartureSelect = forwardRef<CruiseDepartureSelectRef, Props>((props, ref) => (
  <CruiseSearchInputProvider>
    <CruiseDepartureSelectInner {...props} ref={ref} />
  </CruiseSearchInputProvider>
))

CruiseDepartureSelect.displayName = 'CruiseDepartureSelect'

export default CruiseDepartureSelect
